<template>
  <v-container class="ma-0 pa-0">
    <v-form ref="form" v-model="valid">
    <v-tabs
      ref="configSelectionTabs"
      slider-size="4"
      height="58"
      v-model="selectedTab"
      v-if="value"
    >
      <v-tab>
        REWARD SETTINGS
      </v-tab>
      <v-tab>
        ENGAGEMENTS &amp; ELIGIBILITY
      </v-tab>

      <v-tabs-items v-model="selectedTab" class="scrollableCard ma-0 pa-0">
        <v-tab-item eager>
          <v-container>
            <v-row class="ma-0">
              <v-col cols="3" class="ma-0 pa-0">
                <text-box-date-picker v-model="value.designStartDate"
                  ref="designStartDate" :disabled="!isEditable || editingExisting" label="Engagement Reward Version Start Date" :rules="[validateUniqueDesignStartDate,validateDesignStartOnOrBeforeDurationStart]"/>
              </v-col>
              <v-col cols="4" class="mt-0 pt-0" v-if="(versionGap && versionGap.gapPresent)" ref="versionGapBlock">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <div v-on="on" class="d-inline-block" >
                        <v-icon class="pr-1" color="warning" :disabled="!isEditable">fa-exclamation-circle</v-icon>
                        <span :class="{'text--disabled': !isEditable}" class="text-subtitle-2">
                            Informational Message: Previous version was not active before this version
                            <span v-if="(versionGap.gapStart == versionGap.gapEnd)">
                              on the date <span class="text-no-wrap font-weight-bold">{{versionGap.gapStart}}.</span>
                            </span>
                            <span v-else>
                              between the dates <span class="text-no-wrap font-weight-bold">{{versionGap.gapStart}} and {{versionGap.gapEnd}}.</span>
                            </span>
                        </span>
                      </div>
                    </template>
                    <span>
                      <p>
                      There is a period of time before this version for which the previous engagement reward <br/>
                      was not in effect.  Often this is intentional.  This message is here to make the gap noticed <br/>
                      in the case that it was not intentional.
                      </p>
                      <p>
                      If no gap was intended, then adjust the prior duration end date to close the gap.
                      </p>
                    </span>
                  </v-tooltip>
              </v-col>
            </v-row>

            <v-row class="ma-0">
              <v-col cols="3">
                <v-text-field
                  ref="engagementRewardName"
                  label="Engagement Reward Name"
                  v-model="value.name"
                  dense
                  :rules="requiredRule"
                  name="rewardName"
                  :disabled="!isEditable"
                  maxLength="100"
                />
              </v-col>
            </v-row>
            <v-row class="ma-0">
              <v-col cols="3">
                <v-select class="mr-2" v-model="value.rewardType" item-text="[1]" item-value="[0]"
                  label="Engagement Reward Type" ref="rewardType" outlined dense hide-details :items="Array.from(EngagementRewardTypeNames.entries())"
                  :rules="requiredRule" :disabled="!isEditable"/>
              </v-col>
            </v-row>
            <v-row class="ma-0">
                <v-col cols="3" >
                  <v-text-field
                    ref="rewardAmount"
                    label="Reward Amount"
                    v-model="value.rewardAmount"
                    prefix="$"
                    type="number"
                    hide-spin-buttons
                    dense
                    hint="ex. 10.00"
                    :rules="requiredAndPositiveRule"
                    name="rewardAmount"
                    :disabled="!isEditable"
                    maxLength="20"
                  />
                </v-col>
            </v-row>
            <v-row class="ma-0">
              <v-col cols="3">
                <v-select class="mr-2" v-model="value.distributionCadence" item-text="[1]" item-value="[0]"
                  label="Reward Distribution Cadence" ref="distributionCadence" outlined dense hide-details :items="Array.from(DistributionCadenceNames.entries())"
                  :rules="requiredRule" :disabled="!isEditable"/>
              </v-col>
            </v-row>
            <v-row class="ma-0">
              <v-col class="pb-0">
                <span class="text-subtitle-1">Duration</span>
                <v-divider/>
                <v-radio-group ref="durationType" v-model="value.durationType" :disabled="!isEditable" :rules="requiredRule">
                  <v-radio label="Set to Active" value="ACTIVE_ALWAYS" ref="setToActiveRadio"/>
                  <v-radio label="Set a specific start and end date" value="ACTIVE_DURING_INTERVAL" ref="setDatesRadio"/>
                </v-radio-group>
              </v-col>
            </v-row>
            <v-slide-y-transition>
              <v-row class="ma-0 hide-details" v-if="value.durationType == 'ACTIVE_DURING_INTERVAL'" >
                <v-col cols="3" class="py-0">
                  <text-box-date-picker v-model="value.startDate" label="Start Date (required)" name="startDate"
                          ref="startDate" :rules="[requiredRule[0], validateIsOnOrBeforeEndDate, validateDurationStartOnOrAfterDesignStart]" :disabled="!isEditable"/>
                </v-col>
                <v-col cols="3" class="py-0">
                  <text-box-date-picker v-model="value.endDate" label="End Date (optional)" :clearable="true"
                          ref="endDate" :rules="[validateIsOnOrAfterStartDate]" :disabled="!isEditable"/>
                </v-col>
              </v-row>
            </v-slide-y-transition>
          </v-container>
        </v-tab-item>

        <v-tab-item eager>
            <v-row class="mx-2">
              <v-col class="pb-0 mt-2">
                  <span class="text-subtitle-1">Account Holder Eligibility</span>
                  <v-divider/>
              </v-col>
            </v-row>
            <v-row class="mx-2">
              <v-col cols="3">
                  <v-select v-model="value.segmentation" item-text="[1]" item-value="[0]"
                    label="Segmentation" ref="segmentation" outlined dense hide-details :items="Array.from(SegmentationNames.entries())"
                    :rules="requiredRule" :disabled="!isEditable"/>
              </v-col>
            </v-row>
            <v-row class="mx-2">
              <v-col cols="3">
                  <product-mapping-select v-model="value.products"
                    label="Product Selection" ref="productSelection" :rules="requiredRule" :disabled="!isEditable" outlined dense/>
              </v-col>
            </v-row>

            <v-row class="mx-2">
              <v-col class="pb-0">
                <span class="text-subtitle-1">Engagement Logic</span>
                <v-divider/>
              </v-col>
            </v-row>
            <v-row class="mx-2 mt-6">
              <v-col cols="1" class="pr-0">
                Qualify
              </v-col>
              <v-col cols="1" class="pl-0">
                <v-text-field v-model="value.qualificationTimes"
                  type="number" label="Number" dense :disabled="!isEditable"
                  :rules="requiredAndPositiveRule" name="qualificationTimes"
                  hide-spin-buttons/>
              </v-col>
              <v-col cols="1" class="px-0">
                times in
              </v-col>
              <v-col cols="1" class="pl-0">
                <v-text-field v-model="value.qualificationPeriods"
                  type="number" label="Number" dense :disabled="!isEditable"
                  :rules="requiredAndPositiveRule" name="qualificationPeriods"
                  hide-spin-buttons/>
              </v-col>
              <v-col cols="2" class="px-0">
                <v-select v-model="value.qualificationPeriodType" item-text="[1]" item-value="[0]"
                    label="Period Type" ref="periodType" outlined dense hide-details :items="Array.from(PeriodTypeNames.entries())"
                    :rules="requiredRule" :disabled="!isEditable"/>
              </v-col>
            </v-row>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
    </v-form>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import TextBoxDatePicker from '@/components/TextBoxDatePicker.vue';
import ProductMappingSelect from '@/components/ProductMappingSelect.vue';
import {
  Component, Prop, Watch, Inject,
} from 'vue-property-decorator';
import {
  EngagementReward, EngagementRewardVersion, EngagementRewardTypeNames, DistributionCadenceNames, SegmentationNames, PeriodTypeNames, EngagementDurationTypeNames, EngagementRewardGapResponse, EngagementRewardResource,
} from '@/api/engagementrewards';
import { DateTool } from '@/util';


@Component({
  computed: mapState(['fiId']),
  components: {
    TextBoxDatePicker, ProductMappingSelect,
  },
})
export default class EngagementRewardEditor extends Vue {
  fiId!: string;

  @Prop()
  value!: EngagementRewardVersion;

  @Prop()
  reward!: EngagementReward;

  selectedTab: number = 0;

  @Prop({ default: true })
  isEditable!: boolean;

  @Prop({ default: true })
  editingExisting!: boolean;

  @Inject('EngagementRewardResource')
  engagementRewardResource!: EngagementRewardResource;

  valid:boolean = true;

  originalStartDate: string | null = null;

  versionGap: EngagementRewardGapResponse | null = null;

  // Make visible to template
  EngagementRewardTypeNames:Map<string, string> = EngagementRewardTypeNames;

  // Make visible to template
  DistributionCadenceNames:Map<string, string> = DistributionCadenceNames;

  // Make visible to template
  SegmentationNames:Map<string, string> = SegmentationNames;

  // Make visible to template
  PeriodTypeNames:Map<string, string> = PeriodTypeNames;

  // Make visible to template
  EngagementDurationTypeNames:Map<string, string> = EngagementDurationTypeNames;

  requiredRule:Array<any> = [(value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'Required';
    }
    return true;
  }];

  requiredAndPositiveRule:Array<any> = [(value: number) => {
    if (!value) {
      return 'Required';
    } if (value <= 0) {
      return 'Must be positive';
    }
    return true;
  }];

  validateUniqueDesignStartDate(value: any) : boolean | string {
    if (value !== null && value.length !== 0 && this.originalStartDate) {
      const valueDate:Date = new Date(value);
      const originalDate:Date = DateTool.parseDate(this.originalStartDate);

      if (valueDate.getTime() !== originalDate.getTime() || (!this.editingExisting && this.isEditable && this.reward.id)) {
        for (let i = 0; i < this.reward.versions.length; i++) {
          if (DateTool.parseDate(this.reward.versions[i].designStartDate).getTime() === valueDate.getTime()) {
            return 'A version of this reward already exists with the given engagement reward version start date.  Please pick a different date.';
          }
        }
      }
    }
    return true;
  }

  validateDesignStartOnOrBeforeDurationStart(value: any) : boolean | string {
    if (value && value.length !== 0 && this.value.startDate && this.value.startDate.length !== 0 && this.value.durationType !== 'ACTIVE_ALWAYS') {
      const designStart:Date = new Date(value);
      const durationStart:Date = DateTool.parseDate(this.value.startDate);

      if (designStart.getTime() > durationStart.getTime()) {
        return 'This engagement reward version start date is after the specified Duration start date.';
      }
    }

    return true;
  }

  validateDurationStartOnOrAfterDesignStart(value: any) : boolean | string {
    if (value && value.length !== 0 && this.value.designStartDate && this.value.designStartDate.length !== 0 && this.value.durationType !== 'ACTIVE_ALWAYS') {
      const durationStart:Date = new Date(value);
      const designStart:Date = DateTool.parseDate(this.value.designStartDate);

      if (designStart.getTime() > durationStart.getTime()) {
        return 'The specified engagement reward duration start is before the engagement reward version start date.  Choose another date on or after the version start.';
      }
    }

    return true;
  }

  validateIsOnOrBeforeEndDate(value:any) : boolean | string {
    if (!this.value.endDate || this.value.endDate.length === 0) {
      return true;
    }

    if (value && value.length > 0) {
      const startDateTime:Date = new Date(value.toString());
      const endDateTime:Date = new Date(this.value.endDate.toString());
      const endDate = new Date(endDateTime.getUTCFullYear(), endDateTime.getUTCMonth(), endDateTime.getUTCDate());
      const startDate = new Date(startDateTime.getUTCFullYear(), startDateTime.getUTCMonth(), startDateTime.getUTCDate());
      if (startDate.getTime() > endDate.getTime()) {
        return 'Start date must be on or before the configured end date.';
      }
    }
    return true;
  }

  validateIsOnOrAfterStartDate(value:any) : boolean | string {
    if (!this.value.startDate || this.value.startDate.length === 0) {
      return true;
    }

    if (value && value.length > 0) {
      const endDateTime:Date = new Date(value.toString());
      const startDateTime:Date = new Date(this.value.startDate.toString());
      const endDate = new Date(endDateTime.getUTCFullYear(), endDateTime.getUTCMonth(), endDateTime.getUTCDate());
      const startDate = new Date(startDateTime.getUTCFullYear(), startDateTime.getUTCMonth(), startDateTime.getUTCDate());

      if (startDate.getTime() > endDate.getTime()) {
        return 'End date must be on or after the configured start date.';
      }
    }
    return true;
  }

  reloadVersionGapInfo() {
    if (!this.value.id || !this.value.designStartDate) {
      return;
    }

    this.engagementRewardResource.getVersionGap(this.fiId, this.value.id!, this.value.designStartDate)
      .then((gapResponse:EngagementRewardGapResponse) => {
        this.versionGap = gapResponse;
      }).catch((error:any) => {
        console.error(`Error looking up version gap ${error}`);
      });
  }

  @Watch('value.durationType')
  @Watch('value.designStartDate')
  @Watch('value.durationType')
  @Watch('value.endDate')
  @Watch('value.startDate')
  async watchDateInputs() {
    await this.$nextTick();
    const aForm : any = (this.$refs.form as any);
    if (aForm) {
      aForm.validate();
    }
  }

  @Watch('value.designStartDate')
  async watchOnlyDesignStartDate() {
    this.reloadVersionGapInfo();
  }

  mounted() {
    this.onWorkingVersionValueChange();
    this.reloadVersionGapInfo();
  }

  @Watch('value', { deep: true })
  onWorkingProductContentChange() {
    this.$emit('input', this.value);
  }

  @Watch('value', { deep: false })
  onWorkingVersionValueChange() {
    this.originalStartDate = this.value.designStartDate;
  }

  @Watch('valid')
  onValidChange() {
    this.$emit('editFormValid', this.valid);
  }
}
</script>

<style lang="scss" scoped>
</style>
